import React from 'react';
import { jsPDF } from 'jspdf';
import { LOGO_BASE64 } from '../lib/pdfAssets';

const FeaturesDocument = ({ onClose }) => {
  
  const generateFeaturesPDF = () => {
    const doc = new jsPDF({
      orientation: 'portrait',
      unit: 'pt',
      format: 'letter'
    });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 50;
    const contentWidth = pageWidth - (margin * 2);
    let yPos = margin;

    // Helper function to add a new page if needed
    const checkPageBreak = (requiredSpace) => {
      if (yPos + requiredSpace > pageHeight - margin) {
        doc.addPage();
        yPos = margin;
        return true;
      }
      return false;
    };

    // Header with logo and black background
    doc.setFillColor(0, 0, 0);
    doc.rect(0, 0, pageWidth, 100, 'F');
    
    // Add logo
    try {
      doc.addImage(LOGO_BASE64, 'PNG', margin, 15, 200, 70);
    } catch (e) {
      // Fallback text if logo fails
      doc.setTextColor(223, 18, 131);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('UnCloned® Offer Calculator', margin, 55);
    }

    // Version info
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.text('Product Features & Capabilities', pageWidth - margin - 150, 50);
    doc.text(`Document Version: ${new Date().toLocaleDateString()}`, pageWidth - margin - 150, 65);

    yPos = 130;

    // Title Section
    doc.setTextColor(223, 18, 131); // Pink brand color
    doc.setFontSize(28);
    doc.setFont('helvetica', 'bold');
    doc.text('Product Features Overview', margin, yPos);
    yPos += 35;

    // Subtitle
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'normal');
    doc.text('Comprehensive Revenue Planning & Traffic Analysis Platform', margin, yPos);
    yPos += 40;

    // Executive Summary
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.setFont('helvetica', 'bold');
    doc.text('Executive Summary', margin, yPos);
    yPos += 20;

    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    const execSummary = `The UnCloned® Offer Calculator is a sophisticated, enterprise-grade revenue planning tool designed to help businesses calculate exactly how much traffic, leads, and conversions they need to achieve their revenue goals. Built with security, scalability, and user experience as core priorities, this platform empowers entrepreneurs, marketers, and business strategists to make data-driven decisions.`;
    
    const execLines = doc.splitTextToSize(execSummary, contentWidth);
    doc.text(execLines, margin, yPos);
    yPos += execLines.length * 14 + 30;

    // Section 1: Core Calculator Features
    checkPageBreak(200);
    doc.setFillColor(223, 18, 131);
    doc.rect(margin, yPos, contentWidth, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('1. Core Calculator Engine', margin + 10, yPos + 17);
    yPos += 40;

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');

    const coreFeatures = [
      { title: 'Revenue Goal Planning', desc: 'Set specific revenue targets with flexible timeframe options (days, weeks, months, quarters, years).' },
      { title: 'Dynamic Pricing Analysis', desc: 'Input your product/service price to calculate exact units needed for goal achievement.' },
      { title: 'Sales Breakdown', desc: 'Automatic calculation of daily, weekly, monthly, quarterly, and annual sales requirements.' },
      { title: 'Traffic Requirements', desc: 'Precise visitor calculations based on your conversion rates and funnel type.' },
      { title: 'Lead Generation Metrics', desc: 'Lead volume calculations for opt-in funnels with website and lead conversion rates.' },
      { title: 'Marketing Cost Analysis', desc: 'ROI projections including Cost Per Lead (CPL), Customer Acquisition Cost (CAC), and ROAS.' }
    ];

    coreFeatures.forEach(feature => {
      checkPageBreak(40);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(223, 18, 131);
      doc.text(`• ${feature.title}:`, margin + 10, yPos);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const descLines = doc.splitTextToSize(feature.desc, contentWidth - 30);
      doc.text(descLines, margin + 20, yPos + 14);
      yPos += 14 + descLines.length * 12 + 8;
    });

    yPos += 20;

    // Section 2: What-If Analysis
    checkPageBreak(180);
    doc.setFillColor(255, 121, 1); // Orange brand color
    doc.rect(margin, yPos, contentWidth, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('2. What-If Analysis Engine', margin + 10, yPos + 17);
    yPos += 40;

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);

    const whatIfFeatures = [
      { title: 'Conversion Rate Scenarios', desc: 'Model the impact of improved website and lead conversion rates on your marketing requirements.' },
      { title: 'Traffic Reduction Analysis', desc: 'See exactly how much less traffic you need when conversion rates improve.' },
      { title: 'Cost Savings Projections', desc: 'Calculate potential marketing cost reductions from optimization efforts.' },
      { title: 'Side-by-Side Comparison', desc: 'Compare current vs. improved scenarios to quantify optimization value.' }
    ];

    whatIfFeatures.forEach(feature => {
      checkPageBreak(40);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 121, 1);
      doc.text(`• ${feature.title}:`, margin + 10, yPos);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const descLines = doc.splitTextToSize(feature.desc, contentWidth - 30);
      doc.text(descLines, margin + 20, yPos + 14);
      yPos += 14 + descLines.length * 12 + 8;
    });

    yPos += 20;

    // Section 3: Organic Reach Calculator
    checkPageBreak(180);
    doc.setFillColor(15, 255, 226); // Teal brand color
    doc.rect(margin, yPos, contentWidth, 25, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('3. Organic Reach Calculator', margin + 10, yPos + 17);
    yPos += 40;

    doc.setFontSize(11);

    const organicFeatures = [
      { title: 'Social Media Analysis', desc: 'Input your follower count and engagement rates to calculate potential organic visitors.' },
      { title: 'Email List Leverage', desc: 'Calculate traffic potential from your email subscribers using open and click rates.' },
      { title: 'Organic Coverage %', desc: 'See what percentage of your traffic needs can be met through organic channels.' },
      { title: 'Traffic Gap Analysis', desc: 'Identify exactly how much paid traffic you need beyond organic reach.' },
      { title: 'Audience Growth Targets', desc: 'Calculate how large your audience needs to grow to achieve goals organically.' }
    ];

    organicFeatures.forEach(feature => {
      checkPageBreak(40);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 200, 180);
      doc.text(`• ${feature.title}:`, margin + 10, yPos);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const descLines = doc.splitTextToSize(feature.desc, contentWidth - 30);
      doc.text(descLines, margin + 20, yPos + 14);
      yPos += 14 + descLines.length * 12 + 8;
    });

    yPos += 20;

    // Section 4: User Management & Authentication
    checkPageBreak(200);
    doc.setFillColor(74, 10, 102); // Purple brand color
    doc.rect(margin, yPos, contentWidth, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('4. User Management & Authentication', margin + 10, yPos + 17);
    yPos += 40;

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);

    const authFeatures = [
      { title: 'Secure JWT Authentication', desc: 'Industry-standard JSON Web Token authentication with automatic token refresh.' },
      { title: 'User Registration', desc: 'Self-service account creation with email verification and password requirements.' },
      { title: 'Brute Force Protection', desc: 'Automatic account lockout after failed login attempts to prevent unauthorized access.' },
      { title: 'Profile Management', desc: 'Users can update their name, email, and profile photo.' },
      { title: 'Password Management', desc: 'Secure password change functionality with current password verification.' },
      { title: '3-Step Onboarding', desc: 'Guided onboarding flow capturing industry, usage type, and primary goals.' }
    ];

    authFeatures.forEach(feature => {
      checkPageBreak(40);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(74, 10, 102);
      doc.text(`• ${feature.title}:`, margin + 10, yPos);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const descLines = doc.splitTextToSize(feature.desc, contentWidth - 30);
      doc.text(descLines, margin + 20, yPos + 14);
      yPos += 14 + descLines.length * 12 + 8;
    });

    yPos += 20;

    // Section 5: Admin Panel
    checkPageBreak(180);
    doc.setFillColor(208, 255, 11); // Yellow/lime brand color
    doc.rect(margin, yPos, contentWidth, 25, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('5. Administrative Dashboard', margin + 10, yPos + 17);
    yPos += 40;

    doc.setFontSize(11);

    const adminFeatures = [
      { title: 'User Overview', desc: 'View all registered users with their profile information and account status.' },
      { title: 'User Details Modal', desc: 'Expandable user cards showing complete profile, onboarding data, and activity.' },
      { title: 'Access Control', desc: 'Activate or deactivate user accounts with immediate effect.' },
      { title: 'Password Reset', desc: 'Generate temporary passwords for users who need account recovery.' },
      { title: 'User Statistics', desc: 'Track total users, active accounts, and onboarding completion rates.' }
    ];

    adminFeatures.forEach(feature => {
      checkPageBreak(40);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(150, 180, 0);
      doc.text(`• ${feature.title}:`, margin + 10, yPos);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const descLines = doc.splitTextToSize(feature.desc, contentWidth - 30);
      doc.text(descLines, margin + 20, yPos + 14);
      yPos += 14 + descLines.length * 12 + 8;
    });

    yPos += 20;

    // Section 6: Data Management
    checkPageBreak(180);
    doc.setFillColor(223, 18, 131);
    doc.rect(margin, yPos, contentWidth, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('6. Data Management & Sessions', margin + 10, yPos + 17);
    yPos += 40;

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);

    const dataFeatures = [
      { title: 'Save Calculation Sessions', desc: 'Save unlimited calculation scenarios with custom names for future reference.' },
      { title: 'Session History', desc: 'Access all previously saved calculations organized by date.' },
      { title: 'Load Previous Sessions', desc: 'Instantly restore any saved calculation to continue analysis.' },
      { title: 'Delete Sessions', desc: 'Remove outdated or unnecessary saved calculations.' },
      { title: 'Complete Data Persistence', desc: 'All inputs and results are preserved exactly as configured.' }
    ];

    dataFeatures.forEach(feature => {
      checkPageBreak(40);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(223, 18, 131);
      doc.text(`• ${feature.title}:`, margin + 10, yPos);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const descLines = doc.splitTextToSize(feature.desc, contentWidth - 30);
      doc.text(descLines, margin + 20, yPos + 14);
      yPos += 14 + descLines.length * 12 + 8;
    });

    yPos += 20;

    // Section 7: PDF Export
    checkPageBreak(180);
    doc.setFillColor(255, 121, 1);
    doc.rect(margin, yPos, contentWidth, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('7. Professional PDF Reports', margin + 10, yPos + 17);
    yPos += 40;

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);

    const pdfFeatures = [
      { title: 'Branded Reports', desc: 'Professional PDF exports featuring your company logo in a black header.' },
      { title: 'Complete Analysis', desc: 'Full calculation results including all metrics, breakdowns, and what-if scenarios.' },
      { title: 'Legal Documentation', desc: 'Automatic inclusion of terms, conditions, and legal notices.' },
      { title: 'Print-Ready Format', desc: 'US Letter size formatting optimized for printing and sharing.' },
      { title: 'Instant Download', desc: 'One-click export with automatic file naming by date.' }
    ];

    pdfFeatures.forEach(feature => {
      checkPageBreak(40);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(255, 121, 1);
      doc.text(`• ${feature.title}:`, margin + 10, yPos);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const descLines = doc.splitTextToSize(feature.desc, contentWidth - 30);
      doc.text(descLines, margin + 20, yPos + 14);
      yPos += 14 + descLines.length * 12 + 8;
    });

    yPos += 20;

    // Section 8: Security Features
    checkPageBreak(180);
    doc.setFillColor(15, 255, 226);
    doc.rect(margin, yPos, contentWidth, 25, 'F');
    doc.setTextColor(0, 0, 0);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('8. Security & IP Protection', margin + 10, yPos + 17);
    yPos += 40;

    doc.setFontSize(11);

    const securityFeatures = [
      { title: 'Server-Side Calculations', desc: 'All proprietary calculation logic executes on secure backend servers, not in browser code.' },
      { title: 'Encrypted Authentication', desc: 'Bcrypt password hashing with secure JWT tokens for session management.' },
      { title: 'HTTPS Encryption', desc: 'All data transmission secured with TLS/SSL encryption.' },
      { title: 'Cookie Security', desc: 'HttpOnly, Secure, and SameSite cookie attributes for maximum protection.' },
      { title: 'Rate Limiting', desc: 'Built-in protection against brute force and denial-of-service attacks.' }
    ];

    securityFeatures.forEach(feature => {
      checkPageBreak(40);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(15, 200, 180);
      doc.text(`• ${feature.title}:`, margin + 10, yPos);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const descLines = doc.splitTextToSize(feature.desc, contentWidth - 30);
      doc.text(descLines, margin + 20, yPos + 14);
      yPos += 14 + descLines.length * 12 + 8;
    });

    yPos += 20;

    // Section 9: Technical Architecture
    checkPageBreak(200);
    doc.setFillColor(74, 10, 102);
    doc.rect(margin, yPos, contentWidth, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('9. Technical Architecture', margin + 10, yPos + 17);
    yPos += 40;

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);

    const techFeatures = [
      { title: 'Frontend', desc: 'React.js with modern hooks, Tailwind CSS for styling, and Shadcn UI components.' },
      { title: 'Backend', desc: 'Python FastAPI framework providing high-performance RESTful APIs.' },
      { title: 'Database', desc: 'MongoDB NoSQL database for flexible, scalable data storage.' },
      { title: 'PDF Generation', desc: 'jsPDF and pdf-lib libraries for client-side PDF creation and merging.' },
      { title: 'Responsive Design', desc: 'Mobile-first design ensuring perfect experience on all devices.' },
      { title: 'Cloud Deployment', desc: 'Kubernetes-orchestrated deployment for reliability and scalability.' }
    ];

    techFeatures.forEach(feature => {
      checkPageBreak(40);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(74, 10, 102);
      doc.text(`• ${feature.title}:`, margin + 10, yPos);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const descLines = doc.splitTextToSize(feature.desc, contentWidth - 30);
      doc.text(descLines, margin + 20, yPos + 14);
      yPos += 14 + descLines.length * 12 + 8;
    });

    yPos += 30;

    // Use Cases Section
    checkPageBreak(200);
    doc.setFillColor(0, 0, 0);
    doc.rect(margin, yPos, contentWidth, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Target Users & Use Cases', margin + 10, yPos + 17);
    yPos += 40;

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(11);

    const useCases = [
      { title: 'Entrepreneurs', desc: 'Plan product launches with precise traffic and sales requirements.' },
      { title: 'Marketing Teams', desc: 'Set realistic campaign goals based on actual conversion data.' },
      { title: 'Business Strategists', desc: 'Model different scenarios to optimize marketing budgets.' },
      { title: 'Sales Managers', desc: 'Align sales targets with marketing capabilities and resources.' },
      { title: 'Consultants', desc: 'Provide clients with data-driven revenue planning recommendations.' },
      { title: 'Investors', desc: 'Evaluate business plans with realistic traffic and conversion assumptions.' }
    ];

    useCases.forEach(useCase => {
      checkPageBreak(40);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(223, 18, 131);
      doc.text(`• ${useCase.title}:`, margin + 10, yPos);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(0, 0, 0);
      const descLines = doc.splitTextToSize(useCase.desc, contentWidth - 30);
      doc.text(descLines, margin + 20, yPos + 14);
      yPos += 14 + descLines.length * 12 + 8;
    });

    // Footer on last page
    yPos = pageHeight - 60;
    doc.setDrawColor(223, 18, 131);
    doc.line(margin, yPos, pageWidth - margin, yPos);
    yPos += 15;
    
    doc.setTextColor(100, 100, 100);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('© 2025-2026 UnCloned® Media. All Rights Reserved.', margin, yPos);
    doc.text('www.offers.unclonedtools.com', pageWidth - margin - 120, yPos);

    // Add page numbers to all pages
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.setFontSize(9);
      doc.setTextColor(150, 150, 150);
      doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin - 40, pageHeight - 20);
    }

    // Save the PDF
    doc.save(`UnCloned_Offer_Calculator_Features_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="relative bg-black/90 border border-[#4A0A66]/50 rounded-2xl p-8 max-w-lg w-full">
        {/* Glow effect */}
        <div className="absolute -inset-0.5 bg-gradient-to-r from-[#DF1283] via-[#4A0A66] to-[#0FFFE2] rounded-2xl opacity-30 blur-sm -z-10" />
        
        <h2 className="text-2xl font-bold text-white mb-4">Download Features Document</h2>
        
        <p className="text-gray-300 mb-6">
          Generate a comprehensive PDF document containing all features and capabilities of the UnCloned® Offer Calculator. 
          Perfect for sharing with investors, clients, or team members.
        </p>

        <div className="space-y-3 text-sm text-gray-400 mb-8">
          <p>• Complete feature breakdown</p>
          <p>• Technical architecture details</p>
          <p>• Security & IP protection info</p>
          <p>• Use cases & target audience</p>
          <p>• Professional formatting with branding</p>
        </div>

        <div className="flex gap-4">
          <button
            onClick={generateFeaturesPDF}
            className="flex-1 py-3 px-4 bg-gradient-to-r from-[#DF1283] to-[#FF7901] text-white font-semibold rounded-lg hover:opacity-90 transition-all"
          >
            Download PDF
          </button>
          <button
            onClick={onClose}
            className="py-3 px-6 border border-[#4A0A66] text-gray-300 rounded-lg hover:bg-[#4A0A66]/20 transition-all"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default FeaturesDocument;
